// Install/uninstall hooks for the developer error-capture overlay.
//
// This module is intentionally framework-agnostic: it accepts a callback
// `(entry) => void` and returns an `uninstall()` function that cleanly
// restores every monkey-patched global. The React component in `index.tsx`
// owns the lifecycle; this file is only the plumbing.
//
// Design notes:
//   - We always defer to the *original* fetch / console method first, so the
//     overlay can never break the calling code's behavior.
//   - We capture response bodies via `response.clone()` so the consumer's
//     `.json()` / `.text()` / streaming reads are unaffected.
//   - Body capture is bounded by content-type and a 2 KB cap.
//   - All capture work is wrapped in try/catch so a bug inside the overlay
//     never throws into the host app.

import type { DevErrorEntry, DevErrorLabel } from "./types";

const RESPONSE_BODY_MAX_BYTES = 2 * 1024; // 2 KB
const CAPTURABLE_BODY_TYPES = ["application/json", "text/"]; // any content-type starting with these

export type CaptureCallback = (entry: Omit<DevErrorEntry, "id" | "timestamp">) => void;

export interface CaptureHandle {
  uninstall: () => void;
}

/** Safe JSON stringifier with circular-reference fallback. */
function safeStringify(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return String(value);
  if (typeof value !== "object") return String(value);
  try {
    const seen = new WeakSet<object>();
    return JSON.stringify(
      value,
      (_key, val) => {
        if (typeof val === "object" && val !== null) {
          if (seen.has(val as object)) return "[Circular]";
          seen.add(val as object);
        }
        if (val instanceof Error) {
          return { name: val.name, message: val.message, stack: val.stack };
        }
        return val;
      },
      2,
    );
  } catch {
    try {
      return String(value);
    } catch {
      return "[unstringifiable]";
    }
  }
}

/** Join console args into a single human-readable string. */
function formatConsoleArgs(args: unknown[]): string {
  return args.map((a) => safeStringify(a)).join(" ");
}

/** Pick a one-line title out of a multi-line string. */
function firstLine(text: string, max = 200): string {
  const oneLine = text.split("\n")[0] ?? text;
  return oneLine.length > max ? `${oneLine.slice(0, max - 1)}…` : oneLine;
}

/** Best-effort URL parsing (handles relative URLs). */
function parseUrl(input: RequestInfo | URL): { href: string; pathname: string } {
  let raw: string;
  if (typeof input === "string") raw = input;
  else if (input instanceof URL) raw = input.toString();
  else raw = input.url;
  try {
    const u = new URL(raw, typeof window !== "undefined" ? window.location.href : "http://localhost/");
    return { href: u.toString(), pathname: u.pathname || raw };
  } catch {
    return { href: raw, pathname: raw };
  }
}

/** Extract HTTP method from fetch args. */
function extractMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof input === "object" && input !== null && "method" in input) {
    const m = (input as Request).method;
    if (m) return m.toUpperCase();
  }
  return "GET";
}

/** Check whether a content-type is text-ish enough to capture a body preview. */
function isCapturableContentType(contentType: string | null): boolean {
  if (!contentType) return false;
  const ct = contentType.toLowerCase();
  return CAPTURABLE_BODY_TYPES.some((prefix) => ct.startsWith(prefix));
}

/**
 * Capture a bounded response body preview (clones the response first so the
 * caller's body stream remains untouched).
 */
async function captureBodyPreview(response: Response): Promise<string | null> {
  try {
    const ct = response.headers.get("content-type");
    if (!isCapturableContentType(ct)) return null;
    const cl = response.headers.get("content-length");
    if (cl !== null) {
      const len = Number.parseInt(cl, 10);
      if (Number.isFinite(len) && len > RESPONSE_BODY_MAX_BYTES) return null;
    }
    const clone = response.clone();
    const text = await clone.text();
    if (text.length > RESPONSE_BODY_MAX_BYTES) {
      return `${text.slice(0, RESPONSE_BODY_MAX_BYTES)}…[truncated]`;
    }
    return text;
  } catch {
    return null;
  }
}

/**
 * Install all capture hooks. Returns a handle whose `uninstall()` restores
 * every patched global and removes window listeners.
 */
export function installCapture(onEntry: CaptureCallback): CaptureHandle {
  // Bail out early on non-browser environments (SSR safety).
  if (typeof window === "undefined") {
    return { uninstall: () => {} };
  }

  // ---- fetch ---------------------------------------------------------------
  const originalFetch = window.fetch.bind(window);

  const patchedFetch: typeof window.fetch = async (...args) => {
    const [input, init] = args as [RequestInfo | URL, RequestInit | undefined];
    const method = extractMethod(input, init);
    const { href, pathname } = parseUrl(input);

    try {
      const response = await originalFetch(...args);
      if (!response.ok) {
        // Fire-and-forget body capture so we don't slow the caller.
        captureBodyPreview(response)
          .then((body) => {
            safeEmit({
              label: "NETWORK",
              title: `${method} ${response.status} ${pathname}`,
              details: {
                method,
                url: href,
                status: response.status,
                statusText: response.statusText,
                contentType: response.headers.get("content-type"),
                body: body ?? "[body not captured]",
              },
            });
          })
          .catch(() => {
            // Even if body capture fails, still record the error.
            safeEmit({
              label: "NETWORK",
              title: `${method} ${response.status} ${pathname}`,
              details: {
                method,
                url: href,
                status: response.status,
                statusText: response.statusText,
              },
            });
          });
      }
      return response;
    } catch (err) {
      // Intentional aborts (route change, unmount, focus-refetch cancel) are cleanup,
      // not failures — surfacing them as NETWORK errors creates noise in the overlay.
      const isAbort =
        (typeof DOMException !== "undefined" && err instanceof DOMException && err.name === "AbortError") ||
        (err instanceof Error && err.name === "AbortError");
      if (!isAbort) {
        safeEmit({
          label: "NETWORK",
          title: `${method} failed ${pathname}`,
          details: {
            method,
            url: href,
            error: err instanceof Error ? err.message : String(err),
            stack: err instanceof Error ? err.stack : undefined,
          },
        });
      }
      throw err;
    }
  };

  window.fetch = patchedFetch;

  // ---- console.error / console.warn ---------------------------------------
  const originalConsoleError = console.error.bind(console);
  const originalConsoleWarn = console.warn.bind(console);

  const patchedConsoleError = (...args: unknown[]) => {
    originalConsoleError(...args);
    safeEmit({
      label: "CONSOLE.ERROR",
      title: firstLine(formatConsoleArgs(args)),
      details: { args: args.map(safeStringify) },
    });
  };

  const patchedConsoleWarn = (...args: unknown[]) => {
    originalConsoleWarn(...args);
    safeEmit({
      label: "CONSOLE.WARN",
      title: firstLine(formatConsoleArgs(args)),
      details: { args: args.map(safeStringify) },
    });
  };

  console.error = patchedConsoleError;
  console.warn = patchedConsoleWarn;

  // ---- window 'error' -----------------------------------------------------
  const onWindowError = (event: ErrorEvent) => {
    safeEmit({
      label: "EXCEPTION",
      title: firstLine(event.message || "Unhandled exception"),
      details: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error instanceof Error ? event.error.stack : undefined,
      },
    });
  };

  // ---- window 'unhandledrejection' ----------------------------------------
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const isErr = reason instanceof Error;
    safeEmit({
      label: "REJECTION",
      title: firstLine(isErr ? reason.message : safeStringify(reason)),
      details: {
        reason: safeStringify(reason),
        stack: isErr ? reason.stack : undefined,
      },
    });
  };

  window.addEventListener("error", onWindowError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  // ---- helpers ------------------------------------------------------------
  function safeEmit(entry: Omit<DevErrorEntry, "id" | "timestamp">): void {
    try {
      onEntry(entry);
    } catch {
      // Never let overlay bugs propagate.
    }
  }

  // ---- uninstall ----------------------------------------------------------
  return {
    uninstall: () => {
      // Only restore if we still own the slot (paranoia: another patcher may
      // have wrapped on top of us; restoring then would unwrap them too).
      if (window.fetch === patchedFetch) window.fetch = originalFetch;
      if (console.error === patchedConsoleError) console.error = originalConsoleError;
      if (console.warn === patchedConsoleWarn) console.warn = originalConsoleWarn;
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    },
  };
}

/** Public helper for ad-hoc reporting; consumed by the React component. */
export function buildAdHocEntry(
  label: DevErrorLabel | undefined,
  message: string,
  details?: Record<string, unknown>,
): Omit<DevErrorEntry, "id" | "timestamp"> {
  return {
    label: label ?? "OTHER",
    title: firstLine(message),
    details: { message, ...(details ?? {}) },
  };
}
